import { Typography, TypographyProps } from '@mui/material';
import { type StructuredContentDocument } from '@repo/supergraph/structuredContent/types';

import { Fragment } from 'react';
import { StructuredContentRenderer } from '../..';
import { getHeadingId } from '../../../TableOfContents/utils';
import { getAllChildrenStrings } from '../../utils';

type AllowedHeadingProps = {
  level: 1 | 2 | 3 | 4 | 5 | 6;
  size?: 5 | 4 | 3 | 2 | 1 | 0;
  children: StructuredContentDocument;
};

export type HeadingProps = StructuredContentDocument<
  AllowedHeadingProps & { excludeFromTOC: boolean }
>;

const headingStyleMap: Record<number, TypographyProps['variant']> = {
  1: 'title',
  2: 'textLargeBold',
  3: 'textLarge',
  4: 'textBold',
  5: 'text',
  6: 'textSmall',
};

export const ContentHeading = (sccProps: HeadingProps) => {
  const { props } = sccProps;
  const component: React.ElementType = `h${props.level}`;
  const variant = headingStyleMap[props.level];

  const childrenString = getAllChildrenStrings(sccProps);

  const anchorLinkId = childrenString ? getHeadingId(childrenString) : '';

  if (!props.children && !component && !childrenString) return;

  const headingProps: { id: string; size?: 5 | 4 | 3 | 2 | 1 | 0; children: StructuredContentDocument } = {
    id: anchorLinkId,
    size: props.size,
    children: props.children,
  };

  return (
    <Typography
      component={component}
      variant={variant}
      {...headingProps}
      sx={{
        '*': {
          fontFamily: 'inherit !important',
          fontSize: 'inherit !important',
          lineHeight: 'inherit !important',
        },
      }}
    >
      {[props.children].flat().map((child, idx) => {
        if (typeof child === 'string') {
          return <Fragment key={idx}>{child}</Fragment>;
        }

        if (child) {
          return <StructuredContentRenderer doc={child} key={idx} />;
        }
      })}
    </Typography>
  );
};
